import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import type {
  AdminChangeProposal,
  AdminUserProposal,
} from './use-active-admin-change-proposal';

type CreateAdminChangeProposalArg = {
  adminUserProposals: AdminUserProposal[];
  // IMPORTANT: this is required and must be either an int OR null.
  // null explicitly means "no change"
  numberOfApprovalsNeeded: number | null;
};

const fetcher = async (
  _key: string,
  { arg }: { arg: CreateAdminChangeProposalArg }
): Promise<AdminChangeProposal> => {
  const { adminUserProposals, numberOfApprovalsNeeded } = arg;
  return await denApi.post<
    AdminChangeProposal,
    {
      adminUserProposals: AdminUserProposal[];
      numberOfApprovalsNeeded: number | null;
    }
  >(`admin/organization/admin-change-proposals`, {
    adminUserProposals,
    numberOfApprovalsNeeded,
  });
};

/**
 * Creates a new admin change proposal to modify organization admin settings.
 * Proposals can add/remove admin users and/or change the approval threshold.
 * Only one active proposal can exist at a time per organization.
 *
 * @param swrOptions - Optional SWR mutation configuration overrides
 * @returns createProposal trigger function, loading state, and error
 */
export function useCreateAdminChangeProposal(
  swrOptions?: SWRMutationConfiguration<
    AdminChangeProposal,
    Error,
    string,
    CreateAdminChangeProposalArg
  >
) {
  const { trigger, isMutating, error } = useSWRMutation<
    AdminChangeProposal,
    Error,
    string,
    CreateAdminChangeProposalArg
  >('admin/organization/admin-change-proposals', fetcher, swrOptions);

  return { createProposal: trigger, isLoading: isMutating, error };
}
